export default function TermsPage() {
  return (
    <div className="min-h-screen pt-32 pb-16 bg-charcoal-deep">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-4 mb-6">
          <span className="h-[3px] w-16 bg-thunder" />
          <span className="text-thunder font-display tracking-[0.4em] text-xs">// LEGAL</span>
        </div>
        <h1 className="font-display font-bold text-white text-5xl sm:text-6xl mb-4">TERMS OF SERVICE</h1>
        <p className="text-white/50 mb-12">Effective Date: April 12, 2026 — Sonz of Thunder Services LLC ("SOTSVC," "we," "us," or "our")</p>

        <div className="max-w-none space-y-8">

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">1. Agreement to Terms</h2>
            <p className="text-white/60">
              By booking a service, visiting sotsvc.com, or communicating with us, you agree to these Terms of Service. If you do not agree, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">2. Services</h2>
            <p className="text-white/60 mb-3">
              SOTSVC provides professional residential and commercial cleaning services in Orlando and Central Florida. Services include but are not limited to: standard cleaning, deep cleaning, move-in/move-out cleaning, and specialty services as listed on our website.
            </p>
            <p className="text-white/60">
              Service availability is subject to our team's schedule. We reserve the right to decline or cancel service at our discretion, in which case any prepaid amounts will be refunded.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">3. Booking, Payment, and Cancellation</h2>
            <ul className="list-disc pl-6 text-white/60 space-y-2">
              <li><strong>Booking:</strong> Services are confirmed upon receipt of your booking request and our confirmation message.</li>
              <li><strong>Payment:</strong> Payment is due at the time of service or as agreed in advance. We accept major credit/debit cards via Stripe.</li>
              <li><strong>Cancellation:</strong> Cancel or reschedule at least 24 hours before your appointment at no charge. Cancellations with less than 24 hours' notice may incur a cancellation fee of up to 50% of the service price.</li>
              <li><strong>No-show:</strong> If we cannot access your property at the scheduled time and you have not notified us, the full service fee may be charged.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">4. Customer Responsibilities</h2>
            <ul className="list-disc pl-6 text-white/60 space-y-1">
              <li>Provide safe access to the property at the scheduled time</li>
              <li>Secure or remove valuables, fragile items, and pets before our arrival</li>
              <li>Disclose any known hazards, biohazards, or conditions requiring special handling</li>
              <li>Ensure our team can work in a safe environment</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">5. Satisfaction Guarantee and Complaints</h2>
            <p className="text-white/60">
              We offer a 100% satisfaction guarantee. If you are not satisfied with our service, notify us within 24 hours of completion. We will return to re-clean the identified areas at no additional charge. Refunds are issued at our sole discretion for situations where a re-clean is not feasible.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">6. Liability</h2>
            <p className="text-white/60 mb-3">
              SOTSVC carries general liability insurance. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 text-white/60 space-y-1">
              <li>Pre-existing damage or conditions not caused by our team</li>
              <li>Items of exceptional value not disclosed prior to service</li>
              <li>Damage caused by improper product use directed by the customer</li>
              <li>Indirect, consequential, or incidental damages</li>
            </ul>
            <p className="text-white/60 mt-3">
              Our total liability for any claim shall not exceed the amount paid for the specific service giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">7. SMS Communications</h2>
            <p className="text-white/60">
              SMS messaging is optional and is not a condition of any purchase, quote, or service. You opt in only by checking the SMS consent box on our forms or by leaving a voicemail at our business line — providing your phone number alone does not opt you in. If you opt in, you may receive appointment confirmations, service quotes, missed-call follow-ups, reminders, and post-service review requests. Reply STOP to opt out or HELP for help at any time. See our <a href="/privacy" className="text-electric hover:text-thunder transition">Privacy Policy</a> and Section 12 for full details.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">8. Intellectual Property</h2>
            <p className="text-white/60">
              All content on sotsvc.com, including text, logos, and images, is owned by or licensed to SOTSVC and may not be reproduced without written permission.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">9. Governing Law</h2>
            <p className="text-white/60">
              These Terms are governed by the laws of the State of Florida. Any disputes will be resolved in the courts of Orange County, Florida.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">10. Changes to Terms</h2>
            <p className="text-white/60">
              We may update these Terms at any time by posting the revised version on this page. Continued use of our services constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">11. Contact Us</h2>
            <p className="text-white/60">
              Sonz of Thunder Services LLC<br />
              Orlando, Florida<br />
              Email: <a href="mailto:legal@sotsvc.com" className="text-electric hover:text-thunder transition">legal@sotsvc.com</a><br />
              Website: <a href="https://sotsvc.com" className="text-electric hover:text-thunder transition">sotsvc.com</a>
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">12. SMS / Text Messaging Program Terms</h2>
            <div className="text-white/60 space-y-4">
              <p>
                <strong>Program Name:</strong> Sonz of Thunder Services SMS
              </p>
              <p>
                <strong>Program Description:</strong> Sonz of Thunder Services LLC operates an SMS messaging program for customers and prospects who have provided express written consent through our online forms or by leaving a voicemail at our business line. Messages include appointment confirmations, service quotes, missed-call text-back responses, scheduling reminders, and post-service review requests.
              </p>
              <p>
                <strong>Message Frequency:</strong> Message frequency varies based on your service activity. Up to 4 messages per month per active customer.
              </p>
              <p>
                <strong>Message and Data Rates:</strong> Message and data rates may apply. Standard carrier rates apply to all messages sent and received. Sonz of Thunder Services LLC is not responsible for any charges incurred from your wireless carrier.
              </p>
              <div>
                <p className="mb-2">
                  <strong>Opt-In:</strong> You opt in to receive SMS messages by:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Checking the TCPA consent checkbox on https://sotsvc.com/contact when submitting a contact, quote, or scheduling form</li>
                  <li>Calling our business line at +1 (407) 743-6278 and leaving a voicemail</li>
                </ul>
              </div>
              <p>
                <strong>Opt-Out Instructions:</strong> You may opt out at any time. To stop receiving messages, reply <strong>STOP</strong>, <strong>STOPALL</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>END</strong>, or <strong>QUIT</strong> to any message we send. You will receive one final confirmation message confirming your opt-out, after which no further messages will be sent.
              </p>
              <div>
                <p className="mb-2">
                  <strong>HELP / Support Instructions:</strong> For help or support, reply <strong>HELP</strong> to any message we send. You may also contact us directly:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Phone: +1 (407) 743-6278</li>
                  <li>Email: support@sotsvc.com</li>
                  <li>Website: https://sotsvc.com</li>
                </ul>
              </div>
              <p>
                <strong>Supported Carriers:</strong> AT&amp;T, Verizon, T-Mobile, Sprint, Boost, U.S. Cellular, MetroPCS, and other major carriers in the United States. Carriers are not liable for delayed or undelivered messages.
              </p>
              <p>
                <strong>Privacy:</strong> Your phone number and message content are handled in accordance with our Privacy Policy at https://sotsvc.com/privacy. We do not share, sell, or rent your phone number or opt-in data to third parties for marketing purposes. Mobile opt-in data and consent records are not shared with affiliates or third parties for marketing or promotional purposes.
              </p>
              <p>
                <strong>Eligibility:</strong> You must be at least 18 years old to opt in to our SMS program. By opting in, you represent that you are at least 18 years of age and the authorized user of the mobile phone number provided.
              </p>
              <p>
                <strong>Changes to These Terms:</strong> Sonz of Thunder Services LLC reserves the right to modify these SMS terms at any time. Material changes will be communicated via SMS or by posting the updated terms at https://sotsvc.com/terms.
              </p>
              <p>
                <strong>Contact:</strong> Sonz of Thunder Services LLC, Apopka, Florida, USA. Email: support@sotsvc.com. Phone: +1 (407) 743-6278.
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
